import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { productsApi, OfferDetail } from '@/api/products';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Pencil,
  Ticket,
  Package,
  School,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export function OfferDetailPage() {
  const { productCode, offerCode } = useParams<{ productCode: string; offerCode: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [offer, setOffer] = useState<OfferDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  useEffect(() => {
    if (productCode && offerCode) {
      fetchOffer(productCode, offerCode);
    }
  }, [productCode, offerCode]);

  const fetchOffer = async (pCode: string, oCode: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await productsApi.getOffer(pCode, oCode);
      setOffer(data);
    } catch (err) {
      setError('Erreur lors du chargement de l\'offre');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-6">
          <Skeleton className="h-32" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate(`/products/${productCode}`)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour au produit
        </Button>
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error || 'Offre non trouvee'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/products" className="hover:text-foreground">Produits</Link>
        <span>/</span>
        <Link to={`/products/${productCode}`} className="hover:text-foreground">{offer.product.name}</Link>
        <span>/</span>
        <span className="text-foreground">{offer.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to={`/products/${productCode}`}>
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Ticket className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                Offre {offer.name}
              </h1>
              <p className="text-sm text-muted-foreground font-mono">{offer.code}</p>
            </div>
            <Badge variant={offer.isActive ? 'default' : 'secondary'} className="ml-2">
              {offer.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        {isAdmin && (
          <Button variant="outline" asChild>
            <Link to={`/products/${productCode}/offers/${offerCode}/edit`}>
              <Pencil className="h-4 w-4" />
              Modifier
            </Link>
          </Button>
        )}
      </div>

      {offer.description && (
        <p className="text-muted-foreground">{offer.description}</p>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{offer.moduleCount}</p>
                <p className="text-sm text-muted-foreground">Modules inclus</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
                <School className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{offer.schoolCount}</p>
                <p className="text-sm text-muted-foreground">Ecoles abonnees</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                <Ticket className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatPrice(offer.priceT1)}</p>
                <p className="text-sm text-muted-foreground">Prix T1 /élève/an</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pricing Summary */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-base">Tarification par tranche</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tranche</TableHead>
                <TableHead className="text-right">Prix total /élève/an</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Badge variant="outline" className="font-mono">T1</Badge>
                  <span className="ml-2 text-muted-foreground">0 - 100 élèves</span>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  {formatPrice(offer.priceT1)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Badge variant="outline" className="font-mono">T2</Badge>
                  <span className="ml-2 text-muted-foreground">101 - 250 élèves</span>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  {formatPrice(offer.priceT2)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Badge variant="outline" className="font-mono">T3</Badge>
                  <span className="ml-2 text-muted-foreground">251 - 500 élèves</span>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  {formatPrice(offer.priceT3)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Badge variant="outline" className="font-mono">T4</Badge>
                  <span className="ml-2 text-muted-foreground">501+ élèves</span>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  {formatPrice(offer.priceT4)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modules Detail */}
      <Card>
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="flex items-center gap-2 text-base">
            <Package className="h-4 w-4 text-primary" />
            Modules ({offer.modules.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {offer.modules.length === 0 ? (
            <p className="text-muted-foreground text-sm p-4">
              Aucun module inclus dans cette offre
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module</TableHead>
                  <TableHead>Inclusion</TableHead>
                  <TableHead className="text-right">T1 (0-100)</TableHead>
                  <TableHead className="text-right">T2 (101-250)</TableHead>
                  <TableHead className="text-right">T3 (251-500)</TableHead>
                  <TableHead className="text-right">T4 (501+)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {offer.modules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <p className="font-medium text-sm">{module.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{module.code}</p>
                    </TableCell>
                    <TableCell>
                      {module.isDefault ? (
                        <Badge variant="secondary">Par défaut</Badge>
                      ) : (
                        <Badge variant="outline">Au choix</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPrice(module.price)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPrice(module.priceT2)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPrice(module.priceT3)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatPrice(module.priceT4)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
